/**
 * Google Click ID (GCLID) utility functions
 * Used for tracking Google Ads attribution
 */

const GCLID_KEY = 'sourcedin_gclid'

/**
 * Capture GCLID from URL parameters and store in localStorage
 * Should be called on page load to capture GCLID from Google Ads
 */
export function captureGclidFromUrl(): string | null {
  const urlParams = new URLSearchParams(window.location.search)
  const gclid = urlParams.get('gclid')
  
  if (gclid) {
    // Store GCLID in localStorage for persistence across page navigation
    localStorage.setItem(GCLID_KEY, gclid)
    
    // Also store timestamp for expiration (GCLID is valid for 30 days)
    const expirationTime = Date.now() + (30 * 24 * 60 * 60 * 1000) // 30 days
    localStorage.setItem(`${GCLID_KEY}_expires`, expirationTime.toString())
    
    console.log('📊 GCLID captured and stored:', gclid)
    return gclid
  }
  
  return null
}

/**
 * Get stored GCLID from localStorage
 * Returns null if GCLID doesn't exist or has expired
 */
export function getStoredGclid(): string | null {
  const gclid = localStorage.getItem(GCLID_KEY)
  const expires = localStorage.getItem(`${GCLID_KEY}_expires`)
  
  if (!gclid || !expires) {
    return null
  }
  
  // Check if GCLID has expired
  const expirationTime = parseInt(expires, 10)
  if (Date.now() > expirationTime) {
    // Remove expired GCLID
    localStorage.removeItem(GCLID_KEY)
    localStorage.removeItem(`${GCLID_KEY}_expires`)
    return null
  }
  
  return gclid
}

/**
 * Clear stored GCLID (useful for testing or when conversion is complete)
 */
export function clearStoredGclid(): void {
  localStorage.removeItem(GCLID_KEY)
  localStorage.removeItem(`${GCLID_KEY}_expires`)
  console.log('📊 GCLID cleared')
}

/**
 * Get GCLID for form submission
 * First tries to capture from URL, then falls back to stored value
 */
export function getGclidForSubmission(): string | undefined {
  // First try to capture from current URL
  const urlGclid = captureGclidFromUrl()
  if (urlGclid) {
    return urlGclid
  }
  
  // Fall back to stored GCLID
  const storedGclid = getStoredGclid()
  return storedGclid || undefined
}
